import { KPIGrid } from "@/components/dashboard/KPIGrid";
import { MetricTicker } from "@/components/dashboard/MetricTicker";
import { PropertyWatchPanel } from "@/components/dashboard/PropertyWatchPanel";
import { InventorySummary } from "@/components/dashboard/InventorySummary";
// ActivityFeed / SystemHealthPanel / AlertsSummary moved to
// /operations per operator F-002 (Operations Dashboard separation).
// SocialPipelineStatus moved to /operations per F-002.
import { DashboardSection } from "@/components/dashboard/DashboardSection";
import { PriceDropsPanel } from "@/components/dashboard/PriceDropsPanel";
import { DistressedSignalsPanel } from "@/components/dashboard/DistressedSignalsPanel";
import { GeoActivityPanel } from "@/components/dashboard/GeoActivityPanel";
import { HeatmapPanel } from "@/components/dashboard/HeatmapPanel";
import { ViewModeToggle } from "@/components/dashboard/ViewModeToggle";

/**
 * Section order (MPPI-00055), top to bottom:
 *
 *   Ticker                            — headline figures
 *   Key Data Panels                   — counters
 *   Property Watch                    — just listed, scoring well
 *   Market Intelligence + Distressed  — price signals, side by side
 *   Geo Activity                      — full width, own row
 *   Property Types + Top Communities
 *   Investor Heatmap
 *
 * Property Watch sits directly under the counters because it is the only
 * panel with a time cost: a good listing that has just appeared is worth
 * seeing before the standing summaries, which do not change hour to hour.
 *
 * Distress moved up beside Price Drops because both are price signals about
 * individual units. It had been sitting a row below among the inventory
 * rollups, which read as a different kind of question entirely.
 *
 */
export function DashboardPage() {
  // Owner-only sections respect the Super/User View toggle, so the owner can
  // check the page as an ordinary admin sees it.

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
        <ViewModeToggle />
      </div>

      {/* TICKER — MPPI-00027, above the counters */}
      <MetricTicker />

      {/* KEY DATA PANELS */}
      <DashboardSection
        title="Overview"
        description="Live counters · refreshes every 30s"
      >
        <KPIGrid />
      </DashboardSection>

      {/* PROPERTY WATCH — MPPI-00025 */}
      <DashboardSection
        title="Property Watch"
        description="Just listed · Deal Score 70+ · last 14 days. For-sale rows are secondary and ready-to-move only; off-plan and developer primary stock is excluded."
      >
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <PropertyWatchPanel kind="sale" title="New — For Sale" />
          <PropertyWatchPanel kind="rent" title="New — Rental" />
        </div>
      </DashboardSection>

      {/* MARKET INTELLIGENCE — price moves and distress together */}
      <DashboardSection
        title="Market Intelligence — secondary & ready to move"
        description="Price moves and seller-wording signals on resale stock a buyer can move into. Off-plan and developer primary inventory is excluded."
      >
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <PriceDropsPanel />
          <DistressedSignalsPanel />
        </div>
      </DashboardSection>

      {/* GEO ACTIVITY — full width; a map is unreadable in a half column. */}
      <DashboardSection
        title="Geo Activity"
        description="Where the market is moving"
      >
        <div className="grid grid-cols-1 gap-3">
          <GeoActivityPanel />
        </div>
      </DashboardSection>

      {/* PROPERTY TYPES + TOP COMMUNITIES */}
      <DashboardSection
        title="Inventory Breakdown"
        description="By property type and by community"
      >
        {/* `CommunitiesPanel` was removed here (MPPI-00655): it listed the
            same communities by the same measure as InventorySummary's "Top
            Communities" directly above it. The component is still used
            elsewhere, so only this placement is gone. */}
        <InventorySummary />
      </DashboardSection>

      {/* INVESTOR HEATMAP */}
      <DashboardSection
        title="Investor Heatmap — secondary & ready to move"
        description="Price, liquidity and return across resale stock a buyer can move into. Off-plan and developer primary inventory is excluded."
      >
        <div className="grid grid-cols-1 gap-3">
          <HeatmapPanel />
        </div>
      </DashboardSection>

      {/* Vehicles section removed 2026-08-19 (MPPI-00154) — the vertical is
          not being pursued. The panel component remains in the codebase,
          unrendered, so restoring it is a one-line change. */}

      {/* Pipeline & Revenue and Leads removed 2026-08-19 (MPPI-00159).
          Their components remain in the codebase, unrendered.

          Worth keeping the old note here, because removing the panels does
          NOT close what it warned about: the endpoints behind them use
          _DASHBOARD_ROLES, which admits every admin role, so any admin can
          still read this data by calling the API directly. Hiding a panel was
          never access control. That remains MPPI-00057. */}

      {/* Operations panel moved to its own /operations page per
          operator F-002. The investor dashboard above stays focused
          on KPIs / leads / market signals; system-health stuff lives
          in the Admin section sidebar. */}
    </div>
  );
}
